import { useAtomValue, useSetAtom } from 'jotai';
import { BusinessProfile, Contract } from '@kheops/graphql';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { currentContextAtom } from '../state';
import { useCreateGroupChannelMutation } from '../mutations/__generated__/createGroupChannel.generated';
import { commonSnackbarPropsAtom } from '../common/state/state';
import useJoinDefaultChannel from '../chats/hooks/useJoinDefaultChannel';
import { ContractsByCompanyIdDocument } from '../queries/__generated__/contractsByCompanyId.generated';
import { ContractsAsSupplierDocument } from '../queries/__generated__/contractsAsSupplier.generated';
import { ContractsAsBuyerDocument } from '../queries/__generated__/contractsAsBuyer.generated';

type JoinOrCreateChatContract = Pick<Contract, 'channel_url' | 'id' | 'buyingCompanyId' | 'supplyingCompanyId'>;

export type UseJoinOrCreateChatReturn = {
  joinOrCreateChat: (
    contract: JoinOrCreateChatContract,
  ) => Promise<void>;
  loading: boolean;
  channelUrl: string | undefined;
  joined: boolean;
};

export function useJoinOrCreateChat(): UseJoinOrCreateChatReturn {
  const { realm } = useAtomValue(currentContextAtom);
  const [createGroupChannel, { loading: createGroupChannelLoading }] = useCreateGroupChannelMutation({ refetchQueries: [ContractsByCompanyIdDocument, ContractsAsSupplierDocument, ContractsAsBuyerDocument] });
  const [joinDefaultChannel, { loading: joinLoading }] = useJoinDefaultChannel();
  const [channelUrl, setChannelUrl] = useState<string>();
  const [joined, setJoined] = useState(false);
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const { t } = useTranslation(['error']);

  const joinOrCreateChat = async (
    contract: JoinOrCreateChatContract,
  ): Promise<void> => {

    if (contract.channel_url) {
      const joinedChannel = await joinDefaultChannel({
        channelUrl: contract.channel_url,
        contractId: contract.id
      });

      if (joinedChannel) {
        setChannelUrl(contract.channel_url);
        setJoined(joinedChannel);
      } else {
        throw new Error(`Could not join channel of url ${contract.channel_url}`);
      }

      return;
    }

    try {
      const { data: mutationData } = await createGroupChannel({
        variables: {
          buyingCompanyId: contract.buyingCompanyId,
          supplyingCompanyId: contract.supplyingCompanyId,
          fromBusinessProfile: realm as unknown as BusinessProfile,
        }
      });

      if (!mutationData) {
        throw new Error('Could not create group channel');
      }

      setChannelUrl(mutationData.createGroupChannel.channelUrl);

    } catch (err) {
      setCommonSnackbarProps({
        label: t('error:an_error_occured'),
        snackbarProps: {
          open: true,
        },
        alertProps: {
          severity: 'error',
        },
      });

      throw err;
    }
  };

  return { 
    joinOrCreateChat, 
    loading: createGroupChannelLoading || joinLoading,
    channelUrl,
    joined,
  };
}